import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const PresenceContext = createContext();

// Simulate presence update interval (in production this would be WebSocket-based)
const PRESENCE_UPDATE_INTERVAL = 30000; // 30 seconds
const HEARTBEAT_INTERVAL = 10000; // 10 seconds

export function PresenceProvider({ children }) {
  const { user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [myStatus, setMyStatus] = useState('online');
  const [lastActivity, setLastActivity] = useState(new Date());

  // Generate mock online users based on current user
  const generateMockPresence = useCallback(() => {
    const mockUsers = [
      { id: '1', name: 'Dr. Sarah Mitchell', role: 'physician', status: 'online', lastSeen: new Date() },
      { id: '2', name: 'Jennifer Adams', role: 'rn', status: 'online', lastSeen: new Date() },
      { id: '3', name: 'Michael Chen', role: 'rn', status: 'away', lastSeen: new Date(Date.now() - 5 * 60 * 1000) },
      { id: '4', name: 'Lisa Thompson', role: 'social_worker', status: 'online', lastSeen: new Date() },
      { id: '5', name: 'Rev. David Wilson', role: 'chaplain', status: 'offline', lastSeen: new Date(Date.now() - 30 * 60 * 1000) },
      { id: '6', name: 'Amanda Rodriguez', role: 'lpn', status: 'online', lastSeen: new Date() },
      { id: '7', name: 'James Parker', role: 'cna', status: 'busy', lastSeen: new Date() },
      { id: '8', name: 'Emily Foster', role: 'admin', status: 'online', lastSeen: new Date() },
    ];

    // Add current user if authenticated
    if (user && user.id) {
      const existingIdx = mockUsers.findIndex(u => u.id === user.id);
      if (existingIdx === -1) {
        mockUsers.unshift({
          id: user.id,
          name: user.name || user.email,
          role: user.role || 'staff',
          status: myStatus,
          lastSeen: lastActivity
        });
      } else {
        mockUsers[existingIdx].status = myStatus;
        mockUsers[existingIdx].lastSeen = lastActivity;
      }
    }

    // Randomly update some statuses to simulate real-time changes
    return mockUsers.map(u => {
      if (u.id === user?.id) return u; // Don't change current user
      const random = Math.random();
      if (random < 0.1) {
        return { ...u, status: 'away', lastSeen: new Date(Date.now() - Math.random() * 10 * 60 * 1000) };
      }
      return u;
    });
  }, [user, myStatus, lastActivity]);

  // Update presence periodically
  useEffect(() => {
    setOnlineUsers(generateMockPresence());

    const interval = setInterval(() => {
      setOnlineUsers(generateMockPresence());
    }, PRESENCE_UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, [generateMockPresence]);

  // Track user activity
  useEffect(() => {
    const handleActivity = () => {
      setLastActivity(new Date());
      if (myStatus === 'away') {
        setMyStatus('online');
      }
    };

    // Auto-set to away after 5 minutes of inactivity
    const inactivityCheck = setInterval(() => {
      const inactive = Date.now() - lastActivity.getTime() > 5 * 60 * 1000;
      if (inactive && myStatus === 'online') {
        setMyStatus('away');
      }
    }, 60000);

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);

    return () => {
      clearInterval(inactivityCheck);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
    };
  }, [lastActivity, myStatus]);

  const updateStatus = (status) => {
    setMyStatus(status);
    setLastActivity(new Date());
  };

  const getOnlineCount = () => onlineUsers.filter(u => u.status === 'online').length;
  const getAwayCount = () => onlineUsers.filter(u => u.status === 'away').length;
  const getBusyCount = () => onlineUsers.filter(u => u.status === 'busy').length;

  const value = {
    onlineUsers,
    myStatus,
    updateStatus,
    lastActivity,
    getOnlineCount,
    getAwayCount,
    getBusyCount,
    isUserOnline: (userId) => {
      const user = onlineUsers.find(u => u.id === userId);
      return user?.status === 'online';
    },
    getUserStatus: (userId) => {
      return onlineUsers.find(u => u.id === userId)?.status || 'offline';
    }
  };

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
}

export function usePresence() {
  const context = useContext(PresenceContext);
  if (!context) {
    throw new Error('usePresence must be used within a PresenceProvider');
  }
  return context;
}

export default PresenceContext;
